import {
  useLayoutEffect,
  Fragment,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Home.module.css";
import CardPrimary from "../../UI/Cards/CardPrimary/CardPrimary";
import CardTransparent from "../../UI/Cards/CardTransparent/CardTransparent";
import NotFound from "../../Components/NotFound/NotFound";
import ConnectSplash from "../../Components/ConnectSplash/ConnectSplash";
import JoinInviteModal from "../../Components/JoinInviteModal/JoinInviteModal";
import Footer from "../../Components/Footer/Footer";
import MusicPlayer from "../../Components/MusicPlayer/MusicPlayer";
import AmbientBubbles from "../../Components/AmbientBubbles/AmbientBubbles";
import MusicCatalog from "../../Components/MusicCatalog/MusicCatalog";
import { CatalogBrowseFilterProvider } from "../../Components/MusicCatalog/CatalogBrowseFilterContext";
import About from "../../Components/About/About";
import NoticeOne from "../../Components/NoticeOne/NoticeOne";
import NoticeTwo from "../../Components/NoticeTwo/NoticeTwo";
import NoticeThree from "../../Components/NoticeThree/NoticeThree";
import BottomBar from "../../Components/BottomBar/BottomBar";
import { ErrorBoundary } from "../../HOC/ErrorHandling/ErrorBoundary/ErrorBoundary";
import { scrollPositionActions } from "../../store/scrollPositionSlice";
import BarLoader from "../../UI/Loaders/BarLoader/BarLoader";
import { pickContentByType } from "../../data/contentPicker";
import { usePreviewStores } from "../../data/CmsPreviewContext";
import {
  clearPreviewOverlay,
  previewBannerCopy,
  previewItemKey,
  previewItemLabel,
  previewSetItems,
  removePreviewItem,
  stripPreviewSearch,
} from "../../data/cmsPreview";
// import logoSymbol from "../../assets/media/symbol-fire-output.svg";
import { ReactComponent as LogoSymbol } from "../../assets/media/symbol-fire.svg";
import logoWithTagline from "../../assets/media/logo-with-tagline.png";
import {
  getListenableCatalogItems,
  isListenableItem,
  isUpcomingRelease,
} from "../../Hooks/catalogPresentation";

/** Flat section chrome — vertical rhythm comes from .home-sections gap. */
const homeSectionCardStyles = {
  background: "transparent",
  padding: 0,
  margin: 0,
  boxShadow: "none",
  borderRadius: 0,
};

const isConnectedLanding = (location) => {
  const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
  if (path === "/connected") return true;
  const params = new URLSearchParams(location.search || "");
  return params.has("connected") || params.has("confirmed");
};

const Home = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const arrivedConnected = isConnectedLanding(location);
  const [connectedSplash, setConnectedSplash] = useState(arrivedConnected);
  const { catalog, content, isPreview, overlay, query } = usePreviewStores();
  const previewItemsForBanner = useMemo(
    () => previewSetItems(overlay),
    [overlay],
  );
  const catalogMetadata = useSelector(
    (state) => state.catalogData.catalogMetadata,
  );
  const loadingStatus = useSelector(
    (state) => state.loadingRequests.pendingLoadRequests,
  );
  // const [scrollToElm, setScrollToElm] = useState(false);
  // const [scrollToSessionResults, setScrollToSessionResults] = useState(false);
  // const [scrollToAnswer, setScrollToAnswer] = useState(false);
  const angledRectangleRef = useRef();
  const dispatch = useDispatch();
  const hideMusicCatalog = false;

  const listenableItems = useMemo(
    () => getListenableCatalogItems(catalog),
    [catalog],
  );
  /** Active song/album/playlist items — drives catalog + whether music is “live”. */
  const hasActiveMusic = listenableItems.length > 0;

  const defaultFeaturedItem = useMemo(
    () => determineDefaultCatalogItem(catalog, catalogMetadata),
    [catalog, catalogMetadata],
  );
  const defaultSourceURLObj = defaultFeaturedItem?.sourceURLObj || null;
  const hasSourceUrls =
    defaultSourceURLObj &&
    Object.values(defaultSourceURLObj).some(
      (url) => url != null && String(url).trim() !== "",
    );
  /** Live embed, or active upcoming drop teaser (future releaseDate). */
  const hasFeaturedContent =
    hasActiveMusic &&
    defaultFeaturedItem &&
    (isUpcomingRelease(defaultFeaturedItem) || hasSourceUrls);

  /** Data finished loading; show Coming soon instead of an empty/broken player. */
  const showComingSoon =
    !props.notFound && loadingStatus === 0 && !hasFeaturedContent;

  ////////////////////////////////////////
  /// Effects
  ////////////////////////////////////////
  useEffect(() => {
    if (arrivedConnected) setConnectedSplash(true);
  }, [arrivedConnected]);

  const dismissConnectedSplash = useCallback(() => {
    setConnectedSplash(false);
    const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
    if (path !== "/" || location.search) {
      navigate("/", { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const exitPreview = useCallback(() => {
    clearPreviewOverlay();
    setConnectedSplash(false);
    navigate(
      { pathname: "/", search: stripPreviewSearch(location.search) },
      { replace: true },
    );
  }, [location.search, navigate]);

  const splashPreview = isPreview && query.type === "connectSplash";
  const showPendingSplash = splashPreview && query.splash === "pending";
  const showConfirmedSplash = splashPreview
    ? query.splash === "confirmed"
    : connectedSplash;
  const previewRequested = query.on;
  const showSignInToPreview = previewRequested && !isPreview;
  const aboutIsActive = Boolean(pickContentByType(content, "about"));

  useLayoutEffect(() => {
    let rafId = 0;

    const updateScrollPosition = () => {
      rafId = 0;
      if (!angledRectangleRef.current) return;

      const rect = angledRectangleRef.current.getBoundingClientRect();
      // Plain object (not DOMRect + JSON clone) — cheaper on the scroll path.
      dispatch(
        scrollPositionActions.updateWelcomeScrollPosition({
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          x: rect.x,
          y: rect.y,
        }),
      );
    };

    // Cap Redux scroll updates to once per frame so the main thread can paint
    // scroll-linked effects (e.g. NoticeOne image scale) while scrolling.
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateScrollPosition);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    updateScrollPosition();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [dispatch]);

  ////////////////////////////////////////
  /// Functionality
  ////////////////////////////////////////
  const checkIfContentSectionActive = (targetSectionName, contentData) =>
    Boolean(pickContentByType(contentData, targetSectionName));

  /**
   * Resolve the welcome/default catalog item (metadata + embeds).
   * Only active listenable items (song / album / playlist) — never inactive
   * or service-only rows.
   */
  function determineDefaultCatalogItem(catalog, catalogMetadata) {
    if (!catalog || typeof catalog !== "object") return null;

    const byMetaId = (metaKey) => {
      if (!catalogMetadata || !Object.hasOwn(catalogMetadata, metaKey))
        return null;
      const id = catalogMetadata[metaKey]?.[1];
      if (id == null) return null;
      const item = catalog[id];
      if (item && isListenableItem(item) && item.sourceURLObj) return item;
      return null;
    };

    const fromDefault = byMetaId("isDefaultPlaylist");
    if (fromDefault) return fromDefault;

    const fromFeatured = byMetaId("isFeaturedPlaylist");
    if (fromFeatured) return fromFeatured;

    const listenable = getListenableCatalogItems(catalog);
    const withSoundcloud = listenable.filter(
      (item) =>
        item.sourceURLObj &&
        Object.hasOwn(item.sourceURLObj, "soundcloud") &&
        String(item.sourceURLObj.soundcloud || "").trim() !== "",
    );
    if (withSoundcloud.length > 0) {
      return withSoundcloud[withSoundcloud.length - 1];
    }

    return (
      listenable.find((item) => {
        if (!item.sourceURLObj || typeof item.sourceURLObj !== "object")
          return false;
        return Object.values(item.sourceURLObj).some(
          (url) => url != null && String(url).trim() !== "",
        );
      }) || null
    );
  }

  ////////////////////////////////////////
  /// Output
  ////////////////////////////////////////
  return (
    <CatalogBrowseFilterProvider>
      <div className={styles["page-wrap"]}>
        {showSignInToPreview && (
          <div className={styles["preview-banner"]} role="status">
            <p className={styles["preview-banner-text"]}>
              Sign in with an admin account to preview unpublished copy.
            </p>
          </div>
        )}
        {isPreview && (
          <div className={styles["preview-banner"]} role="status">
            <div className={styles["preview-banner-main"]}>
              <p className={styles["preview-banner-text"]}>
                {previewBannerCopy(overlay, query)}
              </p>
              {previewItemsForBanner.length > 0 && (
                <ul className={styles["preview-banner-items"]}>
                  {previewItemsForBanner.map((item) => {
                    const label = previewItemLabel(item);
                    return (
                      <li
                        key={previewItemKey(item)}
                        className={styles["preview-banner-chip"]}
                      >
                        <span>{label}</span>
                        <button
                          type="button"
                          className={styles["preview-banner-chip-remove"]}
                          aria-label={`Remove ${label} from preview`}
                          onClick={() =>
                            removePreviewItem({
                              section: item.section,
                              identifier: item.identifier,
                              values: item.values,
                            })
                          }
                        >
                          ×
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <button
              type="button"
              className={styles["preview-banner-exit"]}
              onClick={exitPreview}
            >
              Exit preview
            </button>
          </div>
        )}
        <div className={styles["welcome-section-container"]}>
          <div
            id="welcome-scroll-marker"
            className={styles["angled-rectangle"]}
            ref={angledRectangleRef}
          >
            <div
              className={styles["angled-triangle"]}
              ref={angledRectangleRef}
            ></div>
            <div className={styles["background-video-wrap"]}>
              <div className={styles["bubble"]}></div>
              <div className={styles["bubble"]}></div>
              <div className={styles["bubble"]}></div>
              <div className={styles["bubble"]}></div>
              <div className={styles["bubble"]}></div>
              <div className={styles["bubble"]}></div>
            </div>
          </div>
          <div className={styles["background-triangle"]}></div>
          <div className={styles["decoration-triangle"]}>
            <LogoSymbol />
          </div>
          {/*
          Large brand mark lives in the welcome section so it scrolls away with
          the decoration triangle (native document scroll). Header shows a small
          logo once this element leaves the top of the viewport.
        */}
          <div id="hero-logo" className={styles["hero-logo"]}>
            <a href="/" className={styles["hero-logo-link"]}>
              <img
                src={logoWithTagline}
                alt="Ignite Elysium"
                className={styles["hero-logo-img"]}
              />
            </a>
          </div>

          <Fragment>
            {loadingStatus > 0 && (
              <div className="bar-loader-container">
                <div className="bar-loader-wrap">
                  <BarLoader />
                </div>
              </div>
            )}
            {props.notFound && (
              <CardTransparent>
                <ErrorBoundary>
                  <div className={styles["not-found-wrap"]}>
                    <NotFound />
                  </div>
                </ErrorBoundary>
              </CardTransparent>
            )}
            {showConfirmedSplash && (
              <ConnectSplash
                variant="confirmed"
                onDismiss={splashPreview ? exitPreview : dismissConnectedSplash}
              />
            )}
            {showPendingSplash && (
              <ConnectSplash variant="pending" onDismiss={exitPreview} />
            )}
            {!props.notFound && (
              <JoinInviteModal
                disabled={showConfirmedSplash || showPendingSplash}
              />
            )}
          </Fragment>
          {/*
          FEATURE: welcome-player-chassis (layout 1+2)
          Left welcome keeps the 135° brand angle; player sits in a right-side
          chassis. When no active releases are live, the same chassis holds a
          Coming soon notice (home look without a broken/empty player).
        */}
          {!props.notFound && (
            <Fragment>
              <CardTransparent
                styles={{ overflow: "visible", background: "transparent" }}
              >
                <ErrorBoundary>
                  <div className={styles["welcome-player-stage"]}>
                    <div className={styles["player-chassis"]}>
                      <div
                        className={styles["player-chassis-shell"]}
                        aria-hidden="true"
                      />
                      <div
                        className={styles["player-chassis-border"]}
                        aria-hidden="true"
                      />
                      <div
                        className={styles["player-chassis-edge"]}
                        aria-hidden="true"
                      />
                      <div className={styles["player-viewport"]}>
                        {hasFeaturedContent ? (
                          <div className={styles["main-music-player-wrap"]}>
                            <MusicPlayer
                              title={
                                defaultFeaturedItem?.title || "Featured Music"
                              }
                              catalogItem={defaultFeaturedItem}
                              showNowPlayingHint={
                                !!defaultFeaturedItem?.title &&
                                !isUpcomingRelease(defaultFeaturedItem)
                              }
                              compactMeta={true}
                              maxTags={2}
                              showAbout={true}
                              passedStyles={{ boxShadow: "none" }}
                              sourceURLObj={defaultSourceURLObj}
                              fromHome={true}
                            />
                          </div>
                        ) : showComingSoon ? (
                          <div
                            className={styles["coming-soon"]}
                            role="status"
                            aria-live="polite"
                          >
                            <AmbientBubbles />
                            <p className={styles["coming-soon-eyebrow"]}>
                              Ignite Elysium
                            </p>
                            <h2 className={styles["coming-soon-title"]}>
                              Coming soon
                            </h2>
                            <p className={styles["coming-soon-lead"]}>
                              Music from Ignite Elysium is on the way. Check
                              back soon for new releases.
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </ErrorBoundary>
              </CardTransparent>
            </Fragment>
          )}
        </div>

        {/*
        Content stack: equal spacing via gap (not per-section margin/padding).
        Section wrappers use flat chrome so only this gap controls rhythm.
      */}
        <div className={styles["home-sections"]}>
          {checkIfContentSectionActive("noticeOne", content) && (
            <CardPrimary
              styles={{
                ...homeSectionCardStyles,
                // marginBottom: "-1.25rem",
              }}
            >
              <ErrorBoundary>
                <NoticeOne />
              </ErrorBoundary>
            </CardPrimary>
          )}
          {!hideMusicCatalog && hasActiveMusic && (
            <CardTransparent styles={homeSectionCardStyles}>
              <ErrorBoundary>
                <MusicCatalog
                  catalogData={{ catalog: catalog }}
                  hideServiceSelector={false}
                  musicIsActive={props.musicIsActive}
                />
              </ErrorBoundary>
            </CardTransparent>
          )}
          {checkIfContentSectionActive("noticeTwo", content) && (
            <CardPrimary styles={homeSectionCardStyles}>
              <ErrorBoundary>
                <NoticeTwo />
              </ErrorBoundary>
            </CardPrimary>
          )}
          {aboutIsActive && (
            <CardPrimary styles={homeSectionCardStyles}>
              <ErrorBoundary>
                <About />
              </ErrorBoundary>
            </CardPrimary>
          )}
          {checkIfContentSectionActive("noticeThree", content) && (
            <CardPrimary styles={homeSectionCardStyles}>
              <ErrorBoundary>
                <NoticeThree />
              </ErrorBoundary>
            </CardPrimary>
          )}
        </div>

        <div className={styles["site-footer-wrap"]}>
          <ErrorBoundary>
            <Footer />
          </ErrorBoundary>
        </div>
        <div className={styles["bottom-bar-wrap"]}>
          <BottomBar />
        </div>
      </div>
    </CatalogBrowseFilterProvider>
  );
};

export default Home;

// soundcloud:
// https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/522243030&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true
// appleMusic:
// https://embed.music.apple.com/us/album/prism/1365013619
// spotify:
// https://open.spotify.com/embed/album/19kAnpqFKi7AvGNDll1GVX?utm_source=generator
